import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

import AppSidebarV2 from "../components/layout/AppSidebarV2";
import { appointmentsApi } from "../api/appointmentsApi";
import { sessionsApi } from "../api/sessionsApi";
import { sessionRoomApi } from "../api/sessionRoomApi";
import { useAuth } from "../auth/authStore";
import { useNotifications } from "../notifications/notificationsStore";
import Modal from "../components/ui/Modal";
import { useToast } from "../ui/toast/ToastProvider";

const PAGE_SIZE_OPTIONS = [5, 10, 20];
const ARCHIVED_CONFERENCES_STORAGE_KEY = "conferences:archived:v1";
const CONFERENCE_JOIN_EARLY_MS = 30 * 60 * 1000;

function normalizeArchiveKey(value) {
  const key = String(value || "").trim();
  return key || "";
}

function conferenceArchiveKey(item) {
  return normalizeArchiveKey(item?.roomBookingId || item?.id);
}

function readArchivedConferenceKeys(storageKey) {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.map((value) => normalizeArchiveKey(value)).filter(Boolean));
  } catch {
    return new Set();
  }
}

function writeArchivedConferenceKeys(storageKey, values) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify([...values]));
  } catch {
    // no-op
  }
}

function displayRole(role) {
  const value = String(role || "").toUpperCase();
  if (value === "ADMIN") return "Администратор";
  if (value === "PSYCHOLOGIST") return "Психолог";
  if (value === "MODERATOR") return "Модератор";
  if (value === "CLIENT") return "Клиент";
  return role || "Пользователь";
}

const CONFERENCE_CLOSED_STATUS_SET = new Set(["CLOSED", "ENDED", "COMPLETED", "COMPLETE", "DONE", "FINISHED", "CANCELLED", "CANCELED"]);
const PAYMENT_SUCCESS_STATUS_SET = new Set(["PAID", "SUCCEEDED", "SUCCESS", "CONFIRMED", "APPROVED", "CAPTURED"]);
const PAYMENT_PENDING_STATUS_SET = new Set(["PENDING", "PENDING_PAYMENT", "WAITING_FOR_CAPTURE", "WAITING", "REQUIRES_ACTION"]);

function getDeepValue(source, path) {
  return path.reduce((acc, key) => (acc && typeof acc === "object" ? acc[key] : undefined), source);
}

function normalizeStatusToken(value) {
  return String(value || "").trim().toUpperCase();
}

function pickBooleanToken(...values) {
  for (const value of values) {
    if (typeof value === "boolean") return value;
    if (typeof value === "number" && Number.isFinite(value)) return value !== 0;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (normalized === "true" || normalized === "1") return true;
      if (normalized === "false" || normalized === "0") return false;
    }
  }
  return null;
}

function pickCountValue(...values) {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  }
  return 0;
}

function isPaymentSuccessStatus(value) {
  return PAYMENT_SUCCESS_STATUS_SET.has(normalizeStatusToken(value));
}

function isPaymentPendingStatus(value) {
  return PAYMENT_PENDING_STATUS_SET.has(normalizeStatusToken(value));
}

function pickPaymentStatus(source) {
  const paymentPaths = [
    ["paymentStatus"],
    ["payment_status"],
    ["paymentState"],
    ["payment_state"],
    ["lastPaymentStatus"],
    ["last_payment_status"],
    ["booking", "paymentStatus"],
    ["booking", "payment_status"],
    ["sessionRoom", "paymentStatus"],
    ["sessionRoom", "payment_status"],
    ["session_room", "paymentStatus"],
    ["session_room", "payment_status"],
    ["room", "paymentStatus"],
    ["room", "payment_status"],
    ["payment", "status"],
    ["payment", "state"],
  ];

  return (
    paymentPaths
      .map((path) => normalizeStatusToken(getDeepValue(source, path)))
      .find(Boolean) || ""
  );
}

function pickConferenceStatus(source) {
  const statusPaths = [
    ["sessionRoom", "status"],
    ["session_room", "status"],
    ["room", "status"],
    ["room", "roomStatus"],
    ["room", "room_status"],
    ["sessionRoom", "roomStatus"],
    ["sessionRoom", "room_status"],
    ["sessionRoom", "sessionStatus"],
    ["sessionRoom", "session_status"],
    ["session_room", "roomStatus"],
    ["session_room", "room_status"],
    ["session_room", "sessionStatus"],
    ["session_room", "session_status"],
    ["roomStatus"],
    ["room_status"],
    ["sessionStatus"],
    ["session_status"],
    ["conferenceStatus"],
    ["conference_status"],
    ["status"],
    ["bookingStatus"],
    ["booking_status"],
    ["paymentStatus"],
    ["payment_status"],
    ["booking", "status"],
    ["booking", "bookingStatus"],
    ["booking", "booking_status"],
    ["booking", "paymentStatus"],
    ["booking", "payment_status"],
  ];

  const statuses = statusPaths
    .map((path) => normalizeStatusToken(getDeepValue(source, path)))
    .filter(Boolean);

  const closed = statuses.find((status) => CONFERENCE_CLOSED_STATUS_SET.has(status));
  if (closed) return closed;

  const nonPayment = statuses.find((status) => !status.includes("PAYMENT"));
  return nonPayment || statuses[0] || "";
}

function resolveConferenceKind(source) {
  const typeToken = String(source?.type || source?.bookingType || source?.appointmentType || "").toUpperCase();
  const explicitGroup = typeToken.includes("GROUP") || pickBooleanToken(source?.group, source?.isGroup, source?.is_group) === true;
  const participantCount = pickCountValue(
    source?.clientsCount,
    source?.membersCount,
    source?.participantsCount,
    source?.attendeesCount,
    source?.raw?.clientsCount,
    source?.raw?.membersCount
  );
  const capacity = pickCountValue(
    source?.capacityClients,
    source?.capacity_clients,
    source?.maxParticipants,
    source?.max_participants,
    source?.raw?.capacityClients,
    source?.raw?.capacity_clients
  );
  const hasDirectClientSignal = Boolean(
    source?.clientId
    || source?.client_id
    || source?.clientUserId
    || source?.client_user_id
    || String(
      source?.clientName
      || source?.clientFullName
      || source?.client?.name
      || source?.customerName
      || source?.userName
      || source?.participantName
      || ""
    ).trim()
  );

  if (explicitGroup && (participantCount > 1 || capacity > 1)) return "GROUP";
  if (hasDirectClientSignal) return "DIRECT";
  if (explicitGroup) return "GENERAL";
  return "GENERAL";
}

function conferenceTypeLabel(item, { lowercase = false } = {}) {
  const value = String(item?.type || "").toUpperCase();
  if (value === "GROUP") return lowercase ? "групповая" : "Групповая";
  if (value === "DIRECT") return lowercase ? "индивидуальная" : "Индивидуальная";
  return lowercase ? "встреча" : "Встреча";
}

function normalizeConference(raw) {
  const source = raw && typeof raw === "object" ? raw : {};
  const bookingId =
    source.bookingId ??
    source.booking_id ??
    source.id ??
    source.sessionBookingId ??
    source.session_booking_id ??
    source.groupSessionId ??
    source.group_session_id ??
    source.sessionId ??
    source.session_id ??
    source.booking?.id ??
    null;

  const startAt =
    source.startDateTime ??
    source.start_datetime ??
    source.startsAt ??
    source.startAt ??
    source.sessionStartAt ??
    source.dateTime ??
    source.date ??
    source.sessionAt ??
    null;
  const endAt = source.endDateTime ?? source.end_datetime ?? source.endsAt ?? source.endAt ?? source.sessionEndAt ?? source.finishAt ?? null;
  const type = resolveConferenceKind(source);
  const status = pickConferenceStatus(source);
  const paymentStatus = pickPaymentStatus(source);
  const psychologistName =
    source.psychologistName ||
    source.psychologistFullName ||
    source.psychologist?.name ||
    source.hostName ||
    source.withName ||
    source.specialistName ||
    "";
  const clientName =
    source.clientName ||
    source.clientFullName ||
    source.client?.name ||
    source.customerName ||
    source.userName ||
    source.participantName ||
    "";
  const groupTitle = source.groupSessionTitle || source.groupTitle || source.topic || "";
  const title =
    source.sessionTitle ||
    groupTitle ||
    source.title ||
    (type === "GROUP" ? "Групповая встреча" : type === "DIRECT" ? clientName || "Индивидуальная сессия" : "Встреча");

  const rawId = bookingId == null ? "" : String(bookingId).trim();
  const roomBookingId = /^\d+$/.test(rawId) ? rawId : "";
  const fallbackId = source.groupSessionId ?? source.group_session_id ?? source.sessionId ?? source.session_id ?? rawId;
  const itemId = roomBookingId || (fallbackId == null ? "" : String(fallbackId).trim());
  const participantsCount = Number(source.clientsCount || source.membersCount || source.participantsCount || source.attendeesCount || 0) || 0;

  const canOpen = Boolean(roomBookingId);
  const normalizedStatus = normalizeStatusToken(status);
  let effectiveStatus = normalizedStatus || status;
  if (CONFERENCE_CLOSED_STATUS_SET.has(normalizedStatus)) {
    effectiveStatus = normalizedStatus;
  } else if (isPaymentSuccessStatus(paymentStatus)) {
    effectiveStatus = "PAID";
  } else if (canOpen && normalizedStatus === "PENDING_PAYMENT") {
    // Если комната уже доступна, не показываем устаревшее "ожидает оплаты".
    effectiveStatus = "CONFIRMED";
  }

  return {
    id: itemId,
    roomBookingId,
    canOpen,
    type,
    title: String(title || "Конференция"),
    psychologistName: String(psychologistName || ""),
    clientName: String(clientName || ""),
    groupTitle: String(groupTitle || ""),
    status: effectiveStatus,
    paymentStatus,
    startAt,
    endAt,
    participantsCount,
    raw: source,
  };
}

function parseDate(raw) {
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

const MSK_UTC_OFFSET_HOURS = 3;

function toMoscowDateTimeInputValue(date) {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return "";
  const msk = new Date(value.getTime() + MSK_UTC_OFFSET_HOURS * 60 * 60 * 1000);
  const two = (n) => String(n).padStart(2, "0");
  const y = msk.getUTCFullYear();
  const m = two(msk.getUTCMonth() + 1);
  const d = two(msk.getUTCDate());
  const hh = two(msk.getUTCHours());
  const mm = two(msk.getUTCMinutes());
  return `${y}-${m}-${d}T${hh}:${mm}`;
}

function moscowDateTimeInputToIso(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day) || !Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;

  const utcMs = Date.UTC(year, month - 1, day, hour - MSK_UTC_OFFSET_HOURS, minute, 0, 0);
  const asUtc = new Date(utcMs);
  if (Number.isNaN(asUtc.getTime())) return null;

  const check = toMoscowDateTimeInputValue(asUtc);
  if (check !== raw) return null;
  return asUtc.toISOString();
}

function splitLocalDateTime(value) {
  const raw = String(value || "").trim();
  if (!raw) return { datePart: "", timePart: "" };
  const [datePart = "", timePartRaw = ""] = raw.split("T");
  return {
    datePart: String(datePart || "").trim(),
    timePart: String(timePartRaw || "").trim().slice(0, 5),
  };
}

function normalizeRuDateDraft(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 8);
  if (!digits) return "";
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
}

function toIsoDatePartFromRuDraft(value) {
  const normalized = normalizeRuDateDraft(value);
  const match = normalized.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return "";

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) return "";
  if (day < 1 || day > 31 || month < 1 || month > 12 || year < 2020 || year > 2100) return "";

  const utcMs = Date.UTC(year, month - 1, day, 12, 0, 0, 0);
  const probe = new Date(utcMs);
  if (probe.getUTCFullYear() !== year || probe.getUTCMonth() + 1 !== month || probe.getUTCDate() !== day) return "";

  const two = (n) => String(n).padStart(2, "0");
  return `${year}-${two(month)}-${two(day)}`;
}

function toRuDatePartFromIsoDatePart(value) {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "";
  return `${match[3]}.${match[2]}.${match[1]}`;
}

function normalizeRuTimeDraft(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 4);
  if (!digits) return "";
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

function toNormalizedTimePart(value) {
  const normalized = normalizeRuTimeDraft(value);
  const match = normalized.match(/^(\d{2}):(\d{2})$/);
  if (!match) return "";
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return "";
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return "";
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function formatMoscowDateTimeLabel(value) {
  const iso = moscowDateTimeInputToIso(value);
  if (!iso) return "";
  const utcDate = new Date(iso);
  if (Number.isNaN(utcDate.getTime())) return "";
  const mskDate = new Date(utcDate.getTime() + MSK_UTC_OFFSET_HOURS * 60 * 60 * 1000);
  const dateLabel = new Intl.DateTimeFormat("ru-RU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(mskDate);
  const timeLabel = new Intl.DateTimeFormat("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  }).format(mskDate);
  return `${dateLabel}, ${timeLabel} МСК`;
}

function startOfDay(date) {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  return value;
}

function endOfDay(date) {
  const value = new Date(date);
  value.setHours(23, 59, 59, 999);
  return value;
}

function isSameDay(left, right) {
  return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth() && left.getDate() === right.getDate();
}

function formatDateTimeRange(startRaw, endRaw) {
  const start = parseDate(startRaw);
  const end = parseDate(endRaw);
  if (!start) return "Дата уточняется";
  const dayLabel = isSameDay(start, new Date())
    ? "Сегодня"
    : new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "long" }).format(start);
  const startTime = new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" }).format(start);
  const endTime = end ? new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" }).format(end) : null;
  return endTime ? `${dayLabel}, ${startTime} — ${endTime}` : `${dayLabel}, ${startTime}`;
}

function formatTime(raw) {
  const value = parseDate(raw);
  if (!value) return "—";
  return new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" }).format(value);
}

function formatDuration(startRaw, endRaw) {
  const start = parseDate(startRaw);
  const end = parseDate(endRaw);
  if (!start || !end) return "Время уточняется";
  const minutes = Math.max(1, Math.round((end.getTime() - start.getTime()) / 60000));
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} ч ${rest} мин` : `${hours} ч`;
}

function initials(name) {
  return (
    String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase() || "•"
  );
}

function isCancelled(status) {
  const value = String(status || "").toUpperCase();
  return value === "CANCELLED" || value === "CANCELED";
}

function isCompleted(status) {
  const value = String(status || "").toUpperCase();
  return (
    value === "COMPLETED" ||
    value === "DONE" ||
    value === "CLOSED" ||
    value === "ENDED" ||
    value === "FINISHED"
  );
}

function isArchivedStatus(status) {
  const value = String(status || "").toUpperCase();
  return value === "ARCHIVED" || value === "ARCHIVE";
}

function collectConferenceStatuses(item) {
  const source = item?.raw && typeof item.raw === "object" ? item.raw : {};
  const statusPaths = [
    ["status"],
    ["state"],
    ["roomStatus"],
    ["room_status"],
    ["sessionStatus"],
    ["session_status"],
    ["conferenceStatus"],
    ["conference_status"],
    ["bookingStatus"],
    ["booking_status"],
    ["sessionRoom", "status"],
    ["sessionRoom", "state"],
    ["sessionRoom", "roomStatus"],
    ["sessionRoom", "room_status"],
    ["sessionRoom", "sessionStatus"],
    ["sessionRoom", "session_status"],
    ["session_room", "status"],
    ["session_room", "state"],
    ["session_room", "roomStatus"],
    ["session_room", "room_status"],
    ["session_room", "sessionStatus"],
    ["session_room", "session_status"],
    ["room", "status"],
    ["room", "state"],
    ["room", "roomStatus"],
    ["room", "room_status"],
    ["room", "sessionStatus"],
    ["room", "session_status"],
    ["booking", "status"],
    ["booking", "state"],
    ["booking", "bookingStatus"],
    ["booking", "booking_status"],
    ["booking", "sessionStatus"],
    ["booking", "session_status"],
  ];

  return [
    normalizeStatusToken(item?.status),
    ...statusPaths.map((path) => normalizeStatusToken(getDeepValue(source, path))),
  ].filter(Boolean);
}

function isConferenceEnded(item) {
  if (!item) return false;
  return collectConferenceStatuses(item).some((status) => CONFERENCE_CLOSED_STATUS_SET.has(status));
}

function pickJoinAvailabilityFlag(item) {
  const source = item?.raw && typeof item.raw === "object" ? item.raw : {};
  const values = [
    item?.joinAvailableNow,
    item?.join_available_now,
    item?.aegisJoinAvailableNow,
    item?.aegis_join_available_now,
    getDeepValue(source, ["joinAvailableNow"]),
    getDeepValue(source, ["join_available_now"]),
    getDeepValue(source, ["aegisJoinAvailableNow"]),
    getDeepValue(source, ["aegis_join_available_now"]),
    getDeepValue(source, ["sessionRoom", "joinAvailableNow"]),
    getDeepValue(source, ["sessionRoom", "aegisJoinAvailableNow"]),
    getDeepValue(source, ["session_room", "joinAvailableNow"]),
    getDeepValue(source, ["session_room", "aegisJoinAvailableNow"]),
    getDeepValue(source, ["room", "joinAvailableNow"]),
    getDeepValue(source, ["room", "aegisJoinAvailableNow"]),
  ];

  for (const value of values) {
    if (typeof value === "boolean") return value;
    if (value === 1 || value === 0) return Boolean(value);
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (normalized === "true" || normalized === "1") return true;
      if (normalized === "false" || normalized === "0") return false;
    }
  }

  return false;
}

function pickJoinAvailableFromAt(item) {
  const source = item?.raw && typeof item.raw === "object" ? item.raw : {};
  return (
    item?.joinAvailableFromAt ||
    item?.join_available_from_at ||
    item?.aegisJoinAvailableFromAt ||
    item?.aegis_join_available_from_at ||
    getDeepValue(source, ["joinAvailableFromAt"]) ||
    getDeepValue(source, ["join_available_from_at"]) ||
    getDeepValue(source, ["aegisJoinAvailableFromAt"]) ||
    getDeepValue(source, ["aegis_join_available_from_at"]) ||
    getDeepValue(source, ["sessionRoom", "joinAvailableFromAt"]) ||
    getDeepValue(source, ["sessionRoom", "aegisJoinAvailableFromAt"]) ||
    getDeepValue(source, ["session_room", "joinAvailableFromAt"]) ||
    getDeepValue(source, ["session_room", "aegisJoinAvailableFromAt"]) ||
    getDeepValue(source, ["room", "joinAvailableFromAt"]) ||
    getDeepValue(source, ["room", "aegisJoinAvailableFromAt"]) ||
    ""
  );
}

function isConferenceAccessibleNow(item, nowTs = Date.now()) {
  if (!item || isCancelled(item.status) || isConferenceEnded(item)) return false;
  const start = parseDate(item.startAt)?.getTime();
  if (!start) return false;
  const end = parseDate(item.endAt)?.getTime() || start + 50 * 60 * 1000;
  if (nowTs >= end) return false;
  if (pickJoinAvailabilityFlag(item)) return true;
  const availableFromTs = parseDate(pickJoinAvailableFromAt(item))?.getTime();
  if (availableFromTs && nowTs >= availableFromTs) return true;
  return Boolean(item?.canOpen) && nowTs >= start - CONFERENCE_JOIN_EARLY_MS;
}

function isUpcoming(item, nowTs = Date.now()) {
  if (!item || isCancelled(item.status) || isConferenceEnded(item)) return false;
  const start = parseDate(item.startAt)?.getTime();
  return Boolean(start && start >= nowTs);
}

function isPast(item, nowTs = Date.now()) {
  if (!item) return false;
  if (isConferenceEnded(item) && !isCancelled(item.status)) return true;
  if (isCancelled(item.status)) return false;
  const start = parseDate(item.startAt)?.getTime();
  if (!start) return false;
  const end = parseDate(item.endAt)?.getTime() || start + 50 * 60 * 1000;
  return end < nowTs;
}

function statusLabel(itemOrStatus) {
  const isItem = typeof itemOrStatus === "object" && itemOrStatus !== null;
  const value = typeof itemOrStatus === "object"
    ? normalizeStatusToken(itemOrStatus?.status)
    : normalizeStatusToken(itemOrStatus);
  const paymentStatus = typeof itemOrStatus === "object"
    ? normalizeStatusToken(itemOrStatus?.paymentStatus)
    : "";
  const canOpen = typeof itemOrStatus === "object" ? Boolean(itemOrStatus?.canOpen) : false;

  if (isArchivedStatus(value)) return "В архиве";
  if (value === "CANCELLED" || value === "CANCELED") return "Отменена";
  if ((isItem && isConferenceEnded(itemOrStatus)) || value === "COMPLETED" || value === "DONE" || value === "CLOSED" || value === "ENDED" || value === "FINISHED") {
    return "Завершена";
  }
  if (isPaymentSuccessStatus(paymentStatus)) return "Подтверждена";
  if (value === "PAID" || value === "CONFIRMED" || value === "SUCCEEDED") return "Подтверждена";
  if (value === "PENDING_PAYMENT" && canOpen) return "Запланирована";
  if (isPaymentPendingStatus(paymentStatus) && canOpen) return "Запланирована";
  if (value === "PENDING_PAYMENT") return "Ожидает оплаты";
  if (isPaymentPendingStatus(paymentStatus)) return "Ожидает оплаты";
  return value ? "Запланирована" : "Статус уточняется";
}

function mergeConference(prev, next) {
  if (!prev) return next;
  if (!next) return prev;
  const prevPaid = isPaymentSuccessStatus(prev.paymentStatus) || normalizeStatusToken(prev.status) === "PAID";
  const nextPaid = isPaymentSuccessStatus(next.paymentStatus) || normalizeStatusToken(next.status) === "PAID";
  if (nextPaid && !prevPaid) return { ...prev, ...next, canOpen: prev.canOpen || next.canOpen };
  if (prevPaid && !nextPaid) return { ...next, ...prev, canOpen: prev.canOpen || next.canOpen };
  if (next.canOpen && !prev.canOpen) return { ...prev, ...next, canOpen: true };
  if (prev.canOpen && !next.canOpen) return { ...next, ...prev, canOpen: true };
  return { ...prev, ...next, canOpen: prev.canOpen || next.canOpen };
}

function cardTone(item, nowTs) {
  if (isConferenceAccessibleNow(item, nowTs)) return "live";
  if (isPast(item, nowTs)) return "past";
  return "planned";
}

function rowStatusTone(item, nowTs) {
  const value = normalizeStatusToken(item?.status);
  const paymentStatus = normalizeStatusToken(item?.paymentStatus);

  if (isConferenceAccessibleNow(item, nowTs)) return "active";
  if (isCancelled(value)) return "cancelled";
  if (isCompleted(value) || isPast(item, nowTs)) return "done";
  if (value === "PENDING_PAYMENT" || isPaymentPendingStatus(paymentStatus)) {
    return item?.canOpen ? "planned" : "waiting";
  }
  if (isPaymentSuccessStatus(paymentStatus) || value === "PAID" || value === "CONFIRMED" || value === "SUCCEEDED") {
    return "planned";
  }
  return "planned";
}

function canArchiveConference(item, nowTs = Date.now()) {
  if (!item) return false;
  return isPast(item, nowTs) || isCompleted(item.status) || isCancelled(item.status);
}

function prioritySortValue(item, nowTs) {
  if (isConferenceAccessibleNow(item, nowTs)) return 0;
  if (isUpcoming(item, nowTs)) return 1;
  if (isPast(item, nowTs)) return 3;
  return 2;
}

function buildPagination(totalPages, currentPage) {
  if (totalPages <= 1) return [1];
  const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
  const normalized = [...pages].filter((page) => page >= 1 && page <= totalPages).sort((a, b) => a - b);
  const output = [];
  for (let i = 0; i < normalized.length; i += 1) {
    const value = normalized[i];
    const prev = normalized[i - 1];
    if (i > 0 && value - prev > 1) output.push("ellipsis-" + value);
    output.push(value);
  }
  return output;
}

function pluralizeMeetings(value) {
  const count = Math.abs(Number(value) || 0);
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod100 >= 11 && mod100 <= 14) return "встреч";
  if (mod10 === 1) return "встреча";
  if (mod10 >= 2 && mod10 <= 4) return "встречи";
  return "встреч";
}

function formatRelativeConferenceLabel(startAt, nowTs = Date.now()) {
  const start = parseDate(startAt);
  if (!start) return "не назначена";
  const nowDate = new Date(nowTs);
  const tomorrow = new Date(nowTs);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const timeLabel = formatTime(startAt);
  if (isSameDay(start, nowDate)) return `сегодня в ${timeLabel}`;
  if (isSameDay(start, tomorrow)) return `завтра в ${timeLabel}`;
  const dayLabel = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long" }).format(start);
  return `${dayLabel} в ${timeLabel}`;
}

function formatSideCardTimeLabel(startAt, nowTs = Date.now()) {
  const start = parseDate(startAt);
  if (!start) return "";
  const nowDate = new Date(nowTs);
  const tomorrow = new Date(nowTs);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const timeLabel = formatTime(startAt);
  if (isSameDay(start, nowDate)) return `Сегодня, ${timeLabel}`;
  if (isSameDay(start, tomorrow)) return `Завтра, ${timeLabel}`;
  const dayLabel = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long" }).format(start);
  return `${dayLabel}, ${timeLabel}`;
}

function buildVideoMeetingsSummary({
  filter,
  search,
  filteredCount,
  activeCount,
  todayCount,
  nextItem,
  archivedCount,
  historyCount,
  nowTs,
}) {
  const query = String(search || "").trim();
  if (query) {
    if (!filteredCount) return "По запросу ничего не найдено · попробуйте изменить поиск или фильтр";
    return `Найдено ${filteredCount} ${pluralizeMeetings(filteredCount)} по запросу`;
  }

  if (filter === "archive") {
    return `Архивные видеовстречи · ${archivedCount} ${pluralizeMeetings(archivedCount)} в архиве`;
  }

  if (filter === "past") {
    return `Прошедшие видеовстречи · ${filteredCount} ${pluralizeMeetings(filteredCount)} завершено`;
  }

  if (activeCount > 0) {
    return `Сейчас идёт ${activeCount} ${pluralizeMeetings(activeCount)} · Следующая ${formatRelativeConferenceLabel(nextItem?.startAt, nowTs)} · ${historyCount} ${pluralizeMeetings(historyCount)} в истории`;
  }

  if (todayCount > 0) {
    return `Сегодня ${todayCount} ${pluralizeMeetings(todayCount)} · Ближайшая ${formatRelativeConferenceLabel(nextItem?.startAt, nowTs)} · ${historyCount} ${pluralizeMeetings(historyCount)} в истории`;
  }

  if (nextItem) {
    return `Сегодня нет видеовстреч · Ближайшая ${formatRelativeConferenceLabel(nextItem.startAt, nowTs)} · ${historyCount} ${pluralizeMeetings(historyCount)} в истории`;
  }

  return `Сегодня нет видеовстреч · Ближайшая не назначена · ${historyCount} ${pluralizeMeetings(historyCount)} в истории`;
}

function getMeetingPersonMeta(item, userName, roleToken) {
  if (item?.type === "GROUP") {
    return { name: item?.psychologistName || userName, role: displayRole(roleToken) };
  }
  if (item?.clientName) {
    return { name: item.clientName, role: "Клиент" };
  }
  if (item?.psychologistName) {
    return { name: item.psychologistName, role: "Психолог" };
  }
  return { name: userName, role: displayRole(roleToken) };
}

function getMeetingAvailabilityLabel(item, nowTs = Date.now()) {
  if (item?.isArchived) return "Встреча скрыта из основного списка";
  if (isCancelled(item?.status)) return "Встреча отменена";
  if (isConferenceAccessibleNow(item, nowTs)) return "Встреча доступна";
  if (isConferenceEnded(item) || isCompleted(item?.status) || isPast(item, nowTs)) return "Встреча завершена";
  if (!item?.canOpen) return "Ссылка откроется за 30 минут до начала";
  if (normalizeStatusToken(item?.status) === "CONFIRMED" || isPaymentSuccessStatus(item?.paymentStatus)) {
    return "Встреча добавлена в расписание";
  }
  return "Ссылка откроется за 30 минут до начала";
}

function getMeetingTimeMeta(item, nowTs = Date.now()) {
  const start = parseDate(item?.startAt);
  if (!start) {
    return { isUnset: true, main: "Время уточняется", end: "", meta: "" };
  }

  const nowDate = new Date(nowTs);
  const tomorrow = new Date(nowTs);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = isSameDay(start, tomorrow);
  const isHistorical = isCompleted(item?.status) || isPast(item, nowTs);

  return {
    isUnset: false,
    main: isTomorrow ? `Завтра, ${formatTime(item?.startAt)}` : formatTime(item?.startAt),
    end: formatTime(item?.endAt),
    meta: isHistorical && !isSameDay(start, nowDate)
      ? new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long" }).format(start)
      : formatDuration(item?.startAt, item?.endAt),
  };
}

function pickText(...values) {
  for (const value of values) {
    const normalized = String(value ?? "").trim();
    if (normalized) return normalized;
  }
  return "";
}

function extractBookingReference(raw) {
  const source = raw && typeof raw === "object" ? raw : {};
  const booking = source.booking && typeof source.booking === "object" ? source.booking : {};
  const sessionRoom = source.sessionRoom && typeof source.sessionRoom === "object" ? source.sessionRoom : {};
  const room = source.room && typeof source.room === "object" ? source.room : {};
  const candidates = [
    source.bookingId,
    source.booking_id,
    source.sessionBookingId,
    source.session_booking_id,
    source.roomBookingId,
    source.room_booking_id,
    booking.id,
    booking.bookingId,
    booking.booking_id,
    sessionRoom.bookingId,
    sessionRoom.booking_id,
    room.bookingId,
    room.booking_id,
    source.id,
  ];
  for (const value of candidates) {
    const ref = String(value ?? "").trim();
    if (ref) return ref;
  }
  return "";
}

function buildGuestConferenceInviteUrl(rawInviteUrl, roomToken) {
  const token = String(roomToken || "").trim();
  if (!token) return "";
  const fallbackPath = `/conference/room/${encodeURIComponent(token)}`;
  const fallbackAbsolute = `${window.location.origin}${fallbackPath}`;
  const source = String(rawInviteUrl || "").trim();
  if (!source) return fallbackAbsolute;
  try {
    const parsed = new URL(source, window.location.origin);
    const guest = new URL(fallbackPath, window.location.origin);
    ["invite_token", "invite", "token"].forEach((key) => {
      const value = parsed.searchParams.get(key);
      if (value && !guest.searchParams.has(key)) guest.searchParams.set(key, value);
    });
    [
      "relay_transcript",
      "relay_url",
      "relay_token",
      "relay_project",
      "relay_mode",
      "relay_delay_ms",
      "relay_min_entries",
      "relay_timeout_ms",
      "relay_roles",
    ].forEach((key) => {
      const value = parsed.searchParams.get(key);
      if (value && !guest.searchParams.has(key)) guest.searchParams.set(key, value);
    });
    return guest.toString();
  } catch {
    return fallbackAbsolute;
  }
}

function IconSearch() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="9" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.7" />
      <path d="M13.2 13.2 17 17" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function IconPlus() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function IconSessionVideo() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3.5" y="6.5" width="12.5" height="11" rx="3.2" stroke="currentColor" strokeWidth="1.7" />
      <path d="m16 10.5 4.6-2.7a.9.9 0 0 1 1.35.78v7a.9.9 0 0 1-1.35.78L16 13.5v-3Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <circle cx="9.5" cy="12" r="1.2" fill="currentColor" />
    </svg>
  );
}

function IconExternalConference() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M9.2 14.8 7.1 17a3.15 3.15 0 0 1-4.45-4.45l2.9-2.9A3.15 3.15 0 0 1 10 9.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="m14.8 9.2 2.1-2.2a3.15 3.15 0 0 1 4.45 4.45l-2.9 2.9A3.15 3.15 0 0 1 14 14.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="m8.8 15.2 6.4-6.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

export default function Conferences() {
  const navigate = useNavigate();
  const toast = useToast();
  const { me, role } = useAuth();
  const { unreadNotificationsCount = 0, messageUnreadTotal = 0 } = useNotifications();
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [filter, setFilter] = React.useState("all");
  const [sortBy, setSortBy] = React.useState("priority");
  const [search, setSearch] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [nowTs, setNowTs] = React.useState(Date.now());
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(5);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [createTitle, setCreateTitle] = React.useState("");
  const [createStartAt, setCreateStartAt] = React.useState("");
  const [createDateDraft, setCreateDateDraft] = React.useState("");
  const [createTimeDraft, setCreateTimeDraft] = React.useState("");
  const [createDurationMinutes, setCreateDurationMinutes] = React.useState(50);
  const [createKind, setCreateKind] = React.useState("regular");
  const [createGuestLink, setCreateGuestLink] = React.useState("");
  const [createSaving, setCreateSaving] = React.useState(false);
  const [deletingConferenceId, setDeletingConferenceId] = React.useState("");
  const [deleteModalItem, setDeleteModalItem] = React.useState(null);
  const [archivedConferenceKeys, setArchivedConferenceKeys] = React.useState(() => new Set());

  const userName = me?.name || me?.fullName || me?.login || me?.email || "Пользователь";
  const roleToken = String(role || "").toUpperCase();
  const canSchedule = Boolean(roleToken && roleToken !== "CLIENT");
  const archiveStorageKey = React.useMemo(() => {
    const userKey = normalizeArchiveKey(me?.id || me?.userId || me?.email || roleToken || "anon");
    return `${ARCHIVED_CONFERENCES_STORAGE_KEY}:${userKey}`;
  }, [me?.email, me?.id, me?.userId, roleToken]);

  const loadConferences = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [roomsPage, appointments] = await Promise.all([
        sessionRoomApi.listMyRooms({ page: 0, size: 200 }).catch(() => ({ items: [] })),
        appointmentsApi.listMyAll().catch(() => []),
      ]);
      const mergedRaw = [
        ...(Array.isArray(roomsPage?.items) ? roomsPage.items : []),
        ...(Array.isArray(appointments) ? appointments : []),
      ];
      const dedup = new Map();
      mergedRaw.map(normalizeConference).forEach((item) => {
        if (!item?.id) return;
        const key = item.roomBookingId || item.id;
        dedup.set(key, mergeConference(dedup.get(key), item));
      });
      setItems(Array.from(dedup.values()));
    } catch (e) {
      setError(e?.message || "Не удалось загрузить конференции");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    const timer = window.setInterval(() => setNowTs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  React.useEffect(() => {
    setArchivedConferenceKeys(readArchivedConferenceKeys(archiveStorageKey));
  }, [archiveStorageKey]);

  React.useEffect(() => {
    writeArchivedConferenceKeys(archiveStorageKey, archivedConferenceKeys);
  }, [archiveStorageKey, archivedConferenceKeys]);

  React.useEffect(() => {
    loadConferences();
  }, [loadConferences]);

  React.useEffect(() => {
    setPage(1);
  }, [filter, search, sortBy, pageSize]);

  const todayDate = React.useMemo(() => new Date(nowTs), [nowTs]);
  const todayStart = React.useMemo(() => startOfDay(todayDate).getTime(), [todayDate]);
  const todayEnd = React.useMemo(() => endOfDay(todayDate).getTime(), [todayDate]);
  const weekEnd = React.useMemo(() => nowTs + 7 * 24 * 60 * 60 * 1000, [nowTs]);

  const itemsWithArchiveState = React.useMemo(
    () =>
      items.map((item) => {
        const archiveKey = conferenceArchiveKey(item);
        return {
          ...item,
          archiveKey,
          isArchived: isArchivedStatus(item?.status) || Boolean(archiveKey && archivedConferenceKeys.has(archiveKey)),
        };
      }),
    [archivedConferenceKeys, items]
  );
  const visibleItems = React.useMemo(() => itemsWithArchiveState.filter((item) => !item.isArchived), [itemsWithArchiveState]);
  const archivedItemsCount = React.useMemo(() => itemsWithArchiveState.filter((item) => item.isArchived).length, [itemsWithArchiveState]);
  const activeItems = React.useMemo(
    () =>
      visibleItems
        .filter((item) => isConferenceAccessibleNow(item, nowTs))
        .sort((a, b) => (parseDate(a.startAt)?.getTime() || 0) - (parseDate(b.startAt)?.getTime() || 0)),
    [visibleItems, nowTs]
  );
  const upcomingItems = React.useMemo(
    () =>
      visibleItems
        .filter((item) => isUpcoming(item, nowTs))
        .sort((a, b) => (parseDate(a.startAt)?.getTime() || 0) - (parseDate(b.startAt)?.getTime() || 0)),
    [visibleItems, nowTs]
  );
  const nextItem = activeItems[0] || upcomingItems[0] || null;

  const dashboardStats = React.useMemo(() => {
    const todayItems = visibleItems.filter((item) => {
      const ts = parseDate(item.startAt)?.getTime();
      return Boolean(ts && ts >= todayStart && ts <= todayEnd);
    });
    const weekItems = visibleItems.filter((item) => {
      const ts = parseDate(item.startAt)?.getTime();
      return Boolean(ts && ts >= nowTs && ts <= weekEnd);
    });
    return {
      liveCount: activeItems.length,
      nextTime: nextItem ? formatTime(nextItem.startAt) : "—",
      nextLabel: nextItem ? (() => {
        const diffMinutes = Math.max(1, Math.round(((parseDate(nextItem.startAt)?.getTime() || nowTs) - nowTs) / 60000));
        if (isConferenceAccessibleNow(nextItem, nowTs)) return "доступна сейчас";
        return diffMinutes < 60 ? `через ${diffMinutes} мин` : formatDateTimeRange(nextItem.startAt, nextItem.endAt);
      })() : "нет встреч",
      todayCount: todayItems.length,
      weekCount: weekItems.length,
    };
  }, [activeItems.length, nextItem, nowTs, todayEnd, todayStart, visibleItems, weekEnd]);

  const filteredItems = React.useMemo(() => {
    const needle = String(search || "").trim().toLowerCase();
    const sourceItems = filter === "archive"
      ? itemsWithArchiveState.filter((item) => item.isArchived)
      : visibleItems;
    const filtered = sourceItems.filter((item) => {
      const startTs = parseDate(item.startAt)?.getTime() || 0;
      const todayMatch = startTs >= todayStart && startTs <= todayEnd;
      if (filter === "today" && !todayMatch) return false;
      if (filter === "upcoming" && !isUpcoming(item, nowTs)) return false;
      if (filter === "past" && !isPast(item, nowTs)) return false;
      if (filter === "live" && !isConferenceAccessibleNow(item, nowTs)) return false;
      if (!needle) return true;
      return [item.title, item.clientName, item.psychologistName, item.groupTitle, statusLabel(item)]
        .filter(Boolean)
        .some((chunk) => String(chunk).toLowerCase().includes(needle));
    });

    filtered.sort((left, right) => {
      const a = parseDate(left.startAt)?.getTime() || 0;
      const b = parseDate(right.startAt)?.getTime() || 0;

      if (sortBy === "latest") return b - a;
      if (sortBy === "nearest") return a - b;

      const groupA = prioritySortValue(left, nowTs);
      const groupB = prioritySortValue(right, nowTs);
      if (groupA !== groupB) return groupA - groupB;

      if (groupA === 3) return b - a;
      return a - b;
    });

    return filtered;
  }, [filter, itemsWithArchiveState, nowTs, search, sortBy, todayEnd, todayStart, visibleItems]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = filteredItems.length ? (safePage - 1) * pageSize : 0;
  const pageEnd = Math.min(pageStart + pageSize, filteredItems.length);
  const pagedItems = filteredItems.slice(pageStart, pageEnd);
  const pagination = buildPagination(totalPages, safePage);
  const historyCount = itemsWithArchiveState.length;
  const summaryLineText = React.useMemo(
    () =>
      buildVideoMeetingsSummary({
        filter,
        search,
        filteredCount: filteredItems.length,
        activeCount: activeItems.length,
        todayCount: dashboardStats.todayCount,
        nextItem,
        archivedCount: archivedItemsCount,
        historyCount,
        nowTs,
      }),
    [
      activeItems.length,
      archivedItemsCount,
      dashboardStats.todayCount,
      filter,
      filteredItems.length,
      historyCount,
      nextItem,
      nowTs,
      search,
    ]
  );

  const createStartParts = React.useMemo(() => splitLocalDateTime(createStartAt), [createStartAt]);
  const createStartLabel = React.useMemo(() => formatMoscowDateTimeLabel(createStartAt), [createStartAt]);

  const openCreateModal = React.useCallback(() => {
    const rounded = new Date(Date.now() + MSK_UTC_OFFSET_HOURS * 60 * 60 * 1000);
    rounded.setUTCSeconds(0, 0);
    const minute = rounded.getUTCMinutes();
    const addMinutes = (30 - (minute % 30)) % 30 || 30;
    rounded.setUTCMinutes(minute + addMinutes);
    const defaultStart = `${rounded.getUTCFullYear()}-${String(rounded.getUTCMonth() + 1).padStart(2, "0")}-${String(rounded.getUTCDate()).padStart(2, "0")}T${String(rounded.getUTCHours()).padStart(2, "0")}:${String(rounded.getUTCMinutes()).padStart(2, "0")}`;
    setCreateTitle("");
    setCreateStartAt(defaultStart);
    setCreateDateDraft(toRuDatePartFromIsoDatePart(splitLocalDateTime(defaultStart).datePart));
    setCreateTimeDraft(splitLocalDateTime(defaultStart).timePart);
    setCreateDurationMinutes(50);
    setCreateKind("regular");
    setCreateGuestLink("");
    setCreateOpen(true);
  }, []);

  React.useEffect(() => {
    const nextDateDraft = toRuDatePartFromIsoDatePart(createStartParts.datePart);
    const nextTimeDraft = createStartParts.timePart;
    setCreateDateDraft((prev) => (prev === nextDateDraft ? prev : nextDateDraft));
    setCreateTimeDraft((prev) => (prev === nextTimeDraft ? prev : nextTimeDraft));
  }, [createStartParts.datePart, createStartParts.timePart]);

  const onCreateStartDateChange = React.useCallback((draftValue) => {
    const draft = normalizeRuDateDraft(draftValue);
    setCreateDateDraft(draft);
    setCreateGuestLink("");
    if (!draft) {
      setCreateStartAt("");
      return;
    }
    const isoDatePart = toIsoDatePartFromRuDraft(draft);
    if (!isoDatePart) return;
    setCreateStartAt((prev) => {
      const prevParts = splitLocalDateTime(prev);
      const nextTime = prevParts.timePart || "12:00";
      return `${isoDatePart}T${nextTime}`;
    });
  }, []);

  const onCreateStartTimeChange = React.useCallback((draftValue) => {
    const draft = normalizeRuTimeDraft(draftValue);
    setCreateTimeDraft(draft);
    setCreateGuestLink("");
    const nextTime = toNormalizedTimePart(draft);
    if (!nextTime) return;
    setCreateStartAt((prev) => {
      const prevParts = splitLocalDateTime(prev);
      const fallbackDate = splitLocalDateTime(toMoscowDateTimeInputValue(new Date())).datePart;
      const nextDate = prevParts.datePart || fallbackDate;
      return `${nextDate}T${nextTime}`;
    });
  }, []);

  const setCreateDatePreset = React.useCallback((offsetDays) => {
    setCreateStartAt((prev) => {
      const prevParts = splitLocalDateTime(prev);
      const baseMsk = new Date(Date.now() + MSK_UTC_OFFSET_HOURS * 60 * 60 * 1000);
      baseMsk.setUTCSeconds(0, 0);
      baseMsk.setUTCDate(baseMsk.getUTCDate() + Number(offsetDays || 0));
      const nextDatePart = `${baseMsk.getUTCFullYear()}-${String(baseMsk.getUTCMonth() + 1).padStart(2, "0")}-${String(baseMsk.getUTCDate()).padStart(2, "0")}`;
      return `${nextDatePart}T${prevParts.timePart || "12:00"}`;
    });
    setCreateGuestLink("");
  }, []);

  const setCreateStartPreset = React.useCallback((offsetMinutes) => {
    const next = new Date(Date.now() + MSK_UTC_OFFSET_HOURS * 60 * 60 * 1000);
    next.setUTCSeconds(0, 0);
    next.setUTCMinutes(next.getUTCMinutes() + Number(offsetMinutes || 0));
    setCreateStartAt(`${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, "0")}-${String(next.getUTCDate()).padStart(2, "0")}T${String(next.getUTCHours()).padStart(2, "0")}:${String(next.getUTCMinutes()).padStart(2, "0")}`);
    setCreateGuestLink("");
  }, []);

  const resolveGuestLinkForCreatedConference = React.useCallback(async (createdRaw, fallbackTitle = "", fallbackStartAt = "") => {
    const source = createdRaw && typeof createdRaw === "object" ? createdRaw : {};
    let roomToken = pickText(
      source.roomToken,
      source.room_token,
      source.sessionRoomToken,
      source.session_room_token,
      source.sessionRoom?.roomToken,
      source.sessionRoom?.room_token,
      source.room?.roomToken,
      source.room?.room_token
    );

    const bookingRef = extractBookingReference(source);

    if (!roomToken && bookingRef) {
      const room = await sessionRoomApi.openOrGetByBooking(bookingRef).catch(() => sessionRoomApi.openByBooking(bookingRef).catch(() => null));
      roomToken = pickText(room?.roomToken, room?.room_token, room?.token);
    }

    if (!roomToken) {
      const roomsPage = await sessionRoomApi.listMyRooms({ page: 0, size: 80 }).catch(() => null);
      const candidates = Array.isArray(roomsPage?.items) ? roomsPage.items : [];
      const fallbackTitleNorm = String(fallbackTitle || "").trim().toLowerCase();
      const fallbackStartTs = parseDate(fallbackStartAt)?.getTime() || 0;
      const matched = candidates.find((item) => {
        const candidateToken = pickText(item?.roomToken, item?.room_token, item?.token);
        if (!candidateToken) return false;
        const byBookingRef = bookingRef && String(item?.bookingId ?? item?.booking_id ?? "").trim() === bookingRef;
        if (byBookingRef) return true;
        const candidateTitle = String(item?.title || item?.sessionTitle || "").trim().toLowerCase();
        const candidateStartTs = parseDate(item?.sessionStartAt || item?.startAt)?.getTime() || 0;
        const titleMatches = fallbackTitleNorm && candidateTitle === fallbackTitleNorm;
        const startMatches = fallbackStartTs && candidateStartTs && Math.abs(candidateStartTs - fallbackStartTs) < 90 * 1000;
        return Boolean(titleMatches && startMatches);
      });
      roomToken = pickText(matched?.roomToken, matched?.room_token, matched?.token);
    }

    if (!roomToken) return "";

    const invite = await sessionRoomApi.getInviteLink(roomToken).catch(() => null);
    const rawInviteUrl = String(invite?.inviteUrl || "").trim();
    return buildGuestConferenceInviteUrl(rawInviteUrl, roomToken);
  }, []);

  const onCreateSession = React.useCallback(async () => {
    const title = String(createTitle || "").trim();
    const startIso = moscowDateTimeInputToIso(createStartAt);
    const duration = Math.min(180, Math.max(20, Number(createDurationMinutes) || 50));

    if (!title) {
      toast.error("Укажите название сессии");
      return;
    }
    if (!startIso) {
      toast.error("Укажите корректное время старта");
      return;
    }

    const endIso = new Date(new Date(startIso).getTime() + duration * 60 * 1000).toISOString();
    const isExternal = createKind === "external";

    setCreateSaving(true);
    try {
      const created = await sessionsApi.createMyGroupSession({
        title,
        startDateTime: startIso,
        endDateTime: endIso,
        sessionFormat: "ONLINE",
        allowJoin: isExternal,
        capacityClients: 1,
        minCapacity: 1,
        priceAtTime: 0,
      });
      appointmentsApi.invalidateCache?.();
      await loadConferences();
      setFilter("all");

      if (isExternal) {
        const guestLink = await resolveGuestLinkForCreatedConference(created, title, startIso);
        setCreateGuestLink(guestLink);
        if (guestLink) {
          try {
            await navigator.clipboard.writeText(guestLink);
            toast.success("Внешняя конференция создана. Ссылка скопирована.");
          } catch {
            toast.success("Внешняя конференция создана. Скопируйте ссылку в форме.");
          }
        } else {
          toast.info("Внешняя конференция создана. Ссылка появится после синхронизации Aegis.");
        }
        return;
      }

      setCreateOpen(false);
      toast.success("Конференция создана");
    } catch (e) {
      toast.error(e?.message || "Не удалось создать конференцию");
    } finally {
      setCreateSaving(false);
    }
  }, [createDurationMinutes, createKind, createStartAt, createTitle, loadConferences, resolveGuestLinkForCreatedConference, toast]);

  const openDeleteConferenceModal = React.useCallback((item) => {
    const bookingId = String(item?.roomBookingId || "").trim();
    if (!bookingId) {
      toast.error("Не удалось определить конференцию для удаления");
      return;
    }
    setDeleteModalItem({
      bookingId,
      title: String(item?.title || "эту конференцию").trim(),
    });
  }, [toast]);

  const handleDeleteConference = React.useCallback(async () => {
    const bookingId = String(deleteModalItem?.bookingId || "").trim();
    if (!bookingId) {
      toast.error("Не удалось определить конференцию для удаления");
      return;
    }

    try {
      setDeletingConferenceId(bookingId);
      await sessionsApi.updateBookingStatus(bookingId, "CANCELLED");
      appointmentsApi.invalidateCache?.();
      await loadConferences();
      setDeleteModalItem(null);
      toast.success("Конференция удалена");
    } catch (e) {
      toast.error(e?.message || "Не удалось удалить конференцию");
    } finally {
      setDeletingConferenceId("");
    }
  }, [deleteModalItem, loadConferences, toast]);

  const archiveConference = React.useCallback(
    (item) => {
      const key = conferenceArchiveKey(item);
      if (!key) {
        toast.error("Не удалось определить конференцию для архива");
        return;
      }
      if (!canArchiveConference(item, nowTs)) {
        toast.info("В архив можно переносить только прошедшие, завершенные или отменённые конференции");
        return;
      }
      setArchivedConferenceKeys((prev) => {
        if (prev.has(key)) return prev;
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      toast.success("Конференция перенесена в архив");
    },
    [nowTs, toast]
  );

  const restoreConference = React.useCallback(
    (item) => {
      const key = conferenceArchiveKey(item);
      if (!key) return;
      setArchivedConferenceKeys((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      toast.success("Конференция возвращена из архива");
    },
    [toast]
  );

  const sideCardState = React.useMemo(() => {
    if (filter === "archive") {
      return {
        title: "Архив",
        timeLabel: "",
        text: "Здесь хранятся встречи, скрытые из основного списка.",
        buttonLabel: "Все встречи",
        buttonKind: "light",
        onClick: () => setFilter("all"),
        disabled: false,
      };
    }

    if (filter === "past") {
      return {
        title: "Прошедшие встречи",
        timeLabel: "",
        text: "Завершённые видеовстречи доступны в истории.",
        buttonLabel: "Открыть календарь",
        buttonKind: "light",
        onClick: () => navigate("/dashboard/calendar"),
        disabled: false,
      };
    }

    if (nextItem) {
      const nextBookingId = String(nextItem.roomBookingId || "").trim();
      const liveNow = isConferenceAccessibleNow(nextItem, nowTs);
      return {
        title: "Следующая встреча",
        timeLabel: formatSideCardTimeLabel(nextItem.startAt, nowTs),
        text: `${nextItem.title} · ${conferenceTypeLabel(nextItem, { lowercase: true })}`,
        buttonLabel: liveNow ? "Открыть встречу" : "Подробнее",
        buttonKind: "mint",
        onClick: () => {
          if (!nextBookingId) return;
          navigate(`/dashboard/conferences/${encodeURIComponent(nextBookingId)}`);
        },
        disabled: !nextBookingId,
      };
    }

    return {
      title: "Следующая встреча",
      timeLabel: "",
      text: "В расписании пока нет предстоящих видеовстреч.",
      buttonLabel: canSchedule ? "Создать встречу" : "Открыть календарь",
      buttonKind: canSchedule ? "mint" : "light",
      onClick: () => {
        if (canSchedule) openCreateModal();
        else navigate("/dashboard/calendar");
      },
      disabled: false,
    };
  }, [canSchedule, filter, navigate, nextItem, nowTs, openCreateModal]);

  return (
    <div className="psych-dashboard-v2 client-dashboard-v2 podcasts-hub conferences-page video-meetings-page">
      <Helmet prioritizeSeoTags>
        <title>Видеовстречи | Бюро Души</title>
        <meta name="description" content="Видеовстречи в кабинете Бюро Души: активные, ближайшие и прошедшие встречи с удобным поиском и архивом." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="shell shell--shared podcasts-shell video-meetings-shell">
        <AppSidebarV2
          activeKey="conferences"
          mobileOpen={mobileSidebarOpen}
          onCloseMobile={() => setMobileSidebarOpen(false)}
          userName={userName}
          roleLabel={displayRole(role)}
          canOpenLiteratureEditor={roleToken === "ADMIN" || roleToken === "PSYCHOLOGIST" || roleToken === "MODERATOR"}
          notificationsBadge={unreadNotificationsCount}
          chatBadge={messageUnreadTotal}
        />

        <button
          className={`podcasts-overlay ${mobileSidebarOpen ? "is-open" : ""}`}
          type="button"
          onClick={() => setMobileSidebarOpen(false)}
          aria-label="Закрыть меню"
        />

        <div className="video-meetings-app">
          <header className="video-meetings-mobile-header">
            <button
              className="video-meetings-mobile-header__burger"
              type="button"
              onClick={() => setMobileSidebarOpen(true)}
              aria-label="Открыть меню"
            >
              <span />
              <span />
              <span />
            </button>
            <div className="video-meetings-mobile-header__brand">Бюро Души</div>
            <div className="video-meetings-mobile-header__stub" />
          </header>

          <main className="main podcasts-main cd-main video-meetings-main">
            <section className="video-meetings-dashboard">
              <div className="video-meetings-head-card">
                <div>
                  <div className="video-meetings-title">Видеовстречи</div>
                  <div className="video-meetings-desc">
                    Создавайте и управляйте онлайн-встречами с клиентами. Активные и ближайшие встречи отображаются первыми, прошедшие доступны в истории и архиве.
                  </div>
                </div>
                {canSchedule ? (
                  <button type="button" className="video-meetings-create-btn" onClick={openCreateModal}>
                    <IconPlus />
                    <span>Создать встречу</span>
                  </button>
                ) : null}
              </div>

              <div className="video-meetings-summary-line">{summaryLineText}</div>

              <div className="video-meetings-content-grid">
                <div>
                  <div className="video-meetings-control-bar">
                    <div className="video-meetings-control-row">
                      <label className="video-meetings-search-wrap">
                        <span><IconSearch /></span>
                        <input
                          className="video-meetings-search-input"
                          type="text"
                          value={search}
                          onChange={(event) => setSearch(event.target.value)}
                          placeholder="Поиск по встречам, клиентам, группам…"
                        />
                      </label>

                      <label className="video-meetings-sort-wrap">
                        <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                          <option value="priority">Активные сверху</option>
                          <option value="nearest">Сначала ближайшие</option>
                          <option value="latest">Сначала новые</option>
                        </select>
                      </label>
                    </div>

                    <div className="video-meetings-filter-pills" role="tablist" aria-label="Фильтры видеовстреч">
                      {[
                        ["all", "Все"],
                        ["today", "Сегодня"],
                        ["upcoming", "Ближайшие"],
                        ["live", "Активные"],
                        ["past", "Прошедшие"],
                        ["archive", "Архив"],
                      ].map(([value, label]) => (
                        <button
                          type="button"
                          key={value}
                          className={`video-meetings-filter-pill ${filter === value ? "is-active" : ""}`}
                          onClick={() => setFilter(value)}
                        >
                          {value === "archive" && archivedItemsCount ? `${label} (${archivedItemsCount})` : label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {loading ? <div className="video-meetings-empty-list">Загружаем видеовстречи…</div> : null}
                  {!loading && error ? <div className="video-meetings-empty-list is-error">{error}</div> : null}

                  {!loading && !error ? (
                    <>
                      {pagedItems.length ? (
                        <div className="video-meetings-list">
                          {pagedItems.map((item) => {
                            const tone = cardTone(item, nowTs);
                            const badgeTone = rowStatusTone(item, nowTs);
                            const personMeta = getMeetingPersonMeta(item, userName, roleToken);
                            const availabilityLabel = getMeetingAvailabilityLabel(item, nowTs);
                            const timeMeta = getMeetingTimeMeta(item, nowTs);
                            const bookingId = String(item.roomBookingId || "").trim();
                            const liveNow = isConferenceAccessibleNow(item, nowTs);
                            const isDeleting = Boolean(bookingId) && deletingConferenceId === bookingId;
                            const canDelete = canSchedule
                              && Boolean(bookingId)
                              && !isCompleted(item.status)
                              && !isCancelled(item.status);
                            const canArchive = canSchedule
                              && !item.isArchived
                              && canArchiveConference(item, nowTs);
                            return (
                              <article
                                className={`video-meetings-card ${isCancelled(item.status) ? "accent-red" : tone === "past" ? "accent-grey" : "accent-mint"} ${liveNow ? "is-live" : ""}`}
                                key={`${item.id}:${item.type}`}
                              >
                                <div className="video-meetings-card__top">
                                  <div className="video-meetings-card__time">
                                    {timeMeta.isUnset ? (
                                      <div className="video-meetings-card__time-unset">{timeMeta.main}</div>
                                    ) : (
                                      <>
                                        <div className="video-meetings-card__time-main">{timeMeta.main}</div>
                                        <div className="video-meetings-card__time-end">{timeMeta.end}</div>
                                        <div className="video-meetings-card__time-meta">{timeMeta.meta}</div>
                                      </>
                                    )}
                                  </div>

                                  <div className="video-meetings-card__divider" aria-hidden="true" />

                                  <div className="video-meetings-card__person">
                                    <div className="video-meetings-card__avatar">{initials(personMeta.name)}</div>
                                    <div>
                                      <div className="video-meetings-card__person-name">{personMeta.name}</div>
                                      <div className="video-meetings-card__person-role">{personMeta.role}</div>
                                    </div>
                                  </div>
                                </div>

                                <div className="video-meetings-card__main">
                                  <div className="video-meetings-card__badges">
                                    <span className={`video-meetings-badge status-${badgeTone}`}>{statusLabel(item)}</span>
                                    <span className="video-meetings-badge is-type">
                                      {conferenceTypeLabel(item)}
                                    </span>
                                  </div>
                                  <div className="video-meetings-card__name">{item.title}</div>
                                  <div className="video-meetings-card__availability">{availabilityLabel}</div>
                                </div>

                                <div className="video-meetings-card__actions">
                                  <button
                                    type="button"
                                    className={`video-meetings-card__btn ${liveNow ? "is-primary" : ""}`}
                                    disabled={!bookingId}
                                    onClick={() => {
                                      if (!bookingId) return;
                                      navigate(`/dashboard/conferences/${encodeURIComponent(bookingId)}`);
                                    }}
                                  >
                                    {liveNow ? "Открыть встречу" : "Подробнее"}
                                  </button>

                                  {canDelete ? (
                                    <button
                                      type="button"
                                      className="video-meetings-card__btn is-danger"
                                      onClick={() => openDeleteConferenceModal(item)}
                                      disabled={isDeleting}
                                    >
                                      {isDeleting ? "Удаляем..." : "Удалить"}
                                    </button>
                                  ) : null}

                                  {canArchive ? (
                                    <button
                                      type="button"
                                      className="video-meetings-card__btn"
                                      onClick={() => archiveConference(item)}
                                    >
                                      В архив
                                    </button>
                                  ) : null}

                                  {item.isArchived ? (
                                    <button
                                      type="button"
                                      className="video-meetings-card__btn"
                                      onClick={() => restoreConference(item)}
                                    >
                                      Вернуть
                                    </button>
                                  ) : null}
                                </div>
                              </article>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="video-meetings-empty-list">
                          <div className="video-meetings-empty-list__title">
                            {String(search || "").trim() ? "Ничего не найдено" : "Пока нет встреч"}
                          </div>
                          <div className="video-meetings-empty-list__text">
                            {String(search || "").trim()
                              ? "Попробуйте изменить запрос, выбрать другой фильтр или очистить поиск."
                              : "По выбранному фильтру видеовстречи не найдены."}
                          </div>
                          {String(search || "").trim() ? (
                            <button type="button" className="video-meetings-side-btn is-light is-inline" onClick={() => setSearch("")}>
                              Очистить поиск
                            </button>
                          ) : null}
                        </div>
                      )}

                      <div className="video-meetings-pagination">
                        <div className="video-meetings-pagination__info">
                          {filteredItems.length ? `${pageStart + 1}–${pageEnd} из ${filteredItems.length}` : "0–0 из 0"}
                        </div>
                        <div className="video-meetings-pagination__controls">
                          <button type="button" className="video-meetings-page-btn" disabled={safePage <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))}>‹</button>
                          {pagination.map((entry) => (
                            typeof entry === "number" ? (
                              <button
                                type="button"
                                key={entry}
                                className={`video-meetings-page-btn ${entry === safePage ? "is-active" : ""}`}
                                onClick={() => setPage(entry)}
                              >
                                {entry}
                              </button>
                            ) : (
                              <span key={entry} className="video-meetings-page-ellipsis">…</span>
                            )
                          ))}
                          <button type="button" className="video-meetings-page-btn" disabled={safePage >= totalPages} onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}>›</button>
                        </div>
                        <label className="video-meetings-pagination__size">
                          <span>Показывать:</span>
                          <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value) || 5)}>
                            {PAGE_SIZE_OPTIONS.map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </label>
                      </div>
                    </>
                  ) : null}
                </div>

                <aside>
                  <div className="video-meetings-side-card">
                    <div className="video-meetings-side-card__title">{sideCardState.title}</div>
                    <div className="video-meetings-side-card__text">
                      {sideCardState.timeLabel ? (
                        <>
                          <div className="video-meetings-side-card__time">{sideCardState.timeLabel}</div>
                          <div className="video-meetings-side-card__sub">{sideCardState.text}</div>
                        </>
                      ) : (
                        sideCardState.text
                      )}
                    </div>
                    <button
                      type="button"
                      className={`video-meetings-side-btn ${sideCardState.buttonKind === "light" ? "is-light" : "is-mint"}`}
                      onClick={sideCardState.onClick}
                      disabled={sideCardState.disabled}
                    >
                      {sideCardState.buttonLabel}
                    </button>
                  </div>

                  <div className="video-meetings-side-card">
                    <div className="video-meetings-side-card__title">Календарь</div>
                    <div className="video-meetings-side-card__text">
                      Откройте календарь, чтобы посмотреть все записи и свободные окна.
                    </div>
                    <button
                      type="button"
                      className="video-meetings-side-btn is-light"
                      onClick={() => navigate("/dashboard/calendar")}
                    >
                      Открыть календарь
                    </button>
                  </div>
                </aside>
              </div>
            </section>
          </main>
        </div>
      </div>

      <Modal
        open={Boolean(deleteModalItem)}
        className="conference-delete-modal-shell"
        onClose={() => {
          if (deletingConferenceId) return;
          setDeleteModalItem(null);
        }}
        title="Удалить конференцию"
        size="sm"
        cardClassName="conference-delete-modal"
        actions={(
          <>
            <button
              type="button"
              className="conference-ghost-btn"
              onClick={() => setDeleteModalItem(null)}
              disabled={Boolean(deletingConferenceId)}
            >
              Отмена
            </button>
            <button
              type="button"
              className="conference-delete-confirm__submit"
              onClick={handleDeleteConference}
              disabled={Boolean(deletingConferenceId)}
            >
              {deletingConferenceId ? "Удаляем..." : "Удалить"}
            </button>
          </>
        )}
      >
        <div className="conference-delete-confirm">
          <p className="conference-delete-confirm__lead">
            Удалить «{deleteModalItem?.title || "конференцию"}»?
          </p>
          <p className="conference-delete-confirm__text">
            Конференция будет отменена и исчезнет из списка.
          </p>
        </div>
      </Modal>

      <Modal
        open={createOpen && canSchedule}
        className="video-meetings-create-modal-shell"
        onClose={() => {
          if (createSaving) return;
          setCreateOpen(false);
        }}
        title="Создать встречу"
        size="sm"
        cardClassName="video-meetings-create-modal"
        actions={(
          <>
            <button type="button" className="video-meetings-modal-btn is-secondary" onClick={() => setCreateOpen(false)} disabled={createSaving}>
              Отмена
            </button>
            {createKind === "external" && createGuestLink ? (
              <button
                type="button"
                className="video-meetings-modal-btn is-primary"
                onClick={() => window.open(createGuestLink, "_blank", "noopener,noreferrer")}
                disabled={createSaving}
              >
                Войти
              </button>
            ) : (
              <button type="button" className="video-meetings-modal-btn is-primary" onClick={onCreateSession} disabled={createSaving}>
                {createSaving ? "Создание..." : createKind === "external" ? "Создать и получить ссылку" : "Создать"}
              </button>
            )}
          </>
        )}
      >
        <div className="video-meetings-modal-form">
          <div className="video-meetings-field-group">
            <label className="video-meetings-field-label">Название встречи</label>
            <input
              className="video-meetings-field-input"
              value={createTitle}
              onChange={(event) => {
                setCreateTitle(event.target.value);
                setCreateGuestLink("");
              }}
              placeholder="Например: Индивидуальная сессия"
              maxLength={140}
              disabled={createSaving}
            />
          </div>

          <div className="video-meetings-field-group">
            <label className="video-meetings-field-label">Дата и время начала</label>
            <div className="video-meetings-datetime-row">
              <input
                type="text"
                className="video-meetings-field-input"
                value={createDateDraft}
                placeholder="ДД.ММ.ГГГГ"
                inputMode="numeric"
                maxLength={10}
                onChange={(event) => onCreateStartDateChange(event.target.value)}
                disabled={createSaving}
              />
              <input
                type="text"
                className="video-meetings-field-input"
                value={createTimeDraft}
                placeholder="ЧЧ:ММ"
                inputMode="numeric"
                maxLength={5}
                onChange={(event) => onCreateStartTimeChange(event.target.value)}
                disabled={createSaving}
              />
            </div>

            <div className="video-meetings-quick-row" role="group" aria-label="Быстрый выбор даты">
              <button type="button" className="video-meetings-quick-chip" onClick={() => setCreateDatePreset(0)} disabled={createSaving}>Сегодня</button>
              <button type="button" className="video-meetings-quick-chip" onClick={() => setCreateDatePreset(1)} disabled={createSaving}>Завтра</button>
              <button type="button" className="video-meetings-quick-chip" onClick={() => setCreateDatePreset(7)} disabled={createSaving}>+7 дней</button>
            </div>

            <div className="video-meetings-quick-row" role="group" aria-label="Быстрый выбор времени">
              <button type="button" className="video-meetings-quick-chip" onClick={() => setCreateStartPreset(30)} disabled={createSaving}>Через 30 мин</button>
              <button type="button" className="video-meetings-quick-chip" onClick={() => setCreateStartPreset(60)} disabled={createSaving}>Через 1 час</button>
              <button type="button" className="video-meetings-quick-chip" onClick={() => setCreateStartPreset(120)} disabled={createSaving}>Через 2 часа</button>
            </div>

            {createStartLabel ? (
              <div className="video-meetings-time-summary">Выбрано: {createStartLabel}</div>
            ) : null}

            <div className="video-meetings-time-note">
              Время указывается по Москве. Встреча становится доступна за 30 минут до начала.
            </div>
          </div>

          <div className="video-meetings-field-group">
            <label className="video-meetings-field-label">Продолжительность</label>
            <select
              className="video-meetings-field-input"
              value={String(createDurationMinutes)}
              onChange={(event) => {
                setCreateDurationMinutes(Number(event.target.value) || 50);
                setCreateGuestLink("");
              }}
              disabled={createSaving}
            >
              <option value="30">30 минут</option>
              <option value="45">45 минут</option>
              <option value="50">50 минут</option>
              <option value="60">1 час</option>
              <option value="75">1 час 15 минут</option>
              <option value="90">1 час 30 минут</option>
              <option value="120">2 часа</option>
            </select>
            <div className="video-meetings-duration-hint">
              Обычно индивидуальная сессия длится 50 минут. Для групповых встреч можно выбрать больше времени.
            </div>
          </div>

          <div className="video-meetings-field-group">
            <label className="video-meetings-field-label">Тип встречи</label>
            <div className="video-meetings-type-cards" role="radiogroup" aria-label="Тип встречи">
              <button
                type="button"
                className={`video-meetings-type-card ${createKind === "regular" ? "is-active" : ""}`}
                onClick={() => {
                  setCreateKind("regular");
                  setCreateGuestLink("");
                }}
                role="radio"
                aria-checked={createKind === "regular"}
                disabled={createSaving}
              >
                <span className="video-meetings-type-icon" aria-hidden="true"><IconSessionVideo /></span>
                <span className="video-meetings-type-name">Обычная</span>
                <span className="video-meetings-type-desc">Клиент с аккаунтом</span>
              </button>
              <button
                type="button"
                className={`video-meetings-type-card is-lavender ${createKind === "external" ? "is-active" : ""}`}
                onClick={() => {
                  setCreateKind("external");
                  setCreateGuestLink("");
                }}
                role="radio"
                aria-checked={createKind === "external"}
                disabled={createSaving}
              >
                <span className="video-meetings-type-icon" aria-hidden="true"><IconExternalConference /></span>
                <span className="video-meetings-type-name">Внешняя</span>
                <span className="video-meetings-type-desc">Без регистрации</span>
              </button>
            </div>
          </div>

          {createKind === "external" ? (
            <div className="video-meetings-invite-box">
              <div className="video-meetings-invite-box__title">Гостевая ссылка</div>
              <div className="video-meetings-invite-box__row">
                <input
                  className="video-meetings-field-input"
                  readOnly
                  value={createGuestLink || "Ссылка появится после создания конференции"}
                />
                <button
                  type="button"
                  className="video-meetings-side-btn is-light"
                  disabled={!createGuestLink}
                  onClick={async () => {
                    if (!createGuestLink) return;
                    try {
                      await navigator.clipboard.writeText(createGuestLink);
                      toast.success("Ссылка скопирована");
                    } catch {
                      toast.error("Не удалось скопировать ссылку");
                    }
                  }}
                >
                  Скопировать
                </button>
              </div>
              <div className="video-meetings-time-note">
                Клиент входит без регистрации. Ссылка активна во время сессии и ведет на гостевую страницу конференции.
              </div>
            </div>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
